import { FormEvent, useEffect, useState } from "react";
import AppLayout from "../../../../shared/components/AppLayout";
import AuthCard from "../../../../shared/components/AuthCard";
import Button from "../../../../shared/components/Button";
import PasswordInput from "../../../../shared/components/PasswordInput";
import Toast from "../../../../shared/components/Toast";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import { resolvePostLoginHash } from "../../../../shared/lib/loginRedirect";
import { useAuth } from "../../shared/hooks/useAuth";
import { requestPasswordReset } from "../../shared/services/authService";

export default function LoginPage() {
  const { signIn, user } = useAuth();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotIdentifier, setForgotIdentifier] = useState("");
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const redirectAfterLogin = resolvePostLoginHash(window.location.hash);

  useEffect(() => {
    const resetSuccess = window.sessionStorage.getItem(
      "tnp-password-reset-success",
    );
    if (!resetSuccess) return;
    window.sessionStorage.removeItem("tnp-password-reset-success");
    setSuccessMessage(siteTexts.auth.forgotPassword.resetSuccess);
  }, []);

  useEffect(() => {
    if (!user) return;
    window.location.hash = redirectAfterLogin;
  }, [redirectAfterLogin, user]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setSuccessMessage("");
    try {
      await signIn(identifier, password);
      window.location.hash = redirectAfterLogin;
    } catch (_err) {
      setError(siteTexts.auth.login.error);
    }
  };

  const onSubmitForgotPassword = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setSuccessMessage("");
    setForgotSubmitting(true);
    try {
      const result = await requestPasswordReset(forgotIdentifier);
      setSuccessMessage(
        result.message || siteTexts.auth.forgotPassword.genericMessage,
      );
    } catch (_err) {
      setSuccessMessage(siteTexts.auth.forgotPassword.genericMessage);
    } finally {
      setForgotSubmitting(false);
    }
  };

  if (showForgotPassword) {
    return (
      <AppLayout
        title={
          <span className="auth-page-title">
            {siteTexts.auth.forgotPassword.title}
          </span>
        }
        subtitle={siteTexts.auth.forgotPassword.subtitle}
      >
        {successMessage && (
          <Toast
            message={successMessage}
            variant="success"
            onClose={() => setSuccessMessage("")}
          />
        )}
        <AuthCard
          error={error}
          onSubmit={onSubmitForgotPassword}
          submitAction={
            <Button type="submit" variant="primary" disabled={forgotSubmitting}>
              {forgotSubmitting
                ? siteTexts.common.loading
                : siteTexts.auth.forgotPassword.submit}
            </Button>
          }
          footer={
            <p>
              <button
                type="button"
                className="auth-inline-link"
                onClick={() => {
                  setShowForgotPassword(false);
                  setError("");
                  setSuccessMessage("");
                }}
              >
                {siteTexts.auth.forgotPassword.backToLogin}
              </button>
            </p>
          }
        >
          <label className="field">
            <span>{siteTexts.auth.login.identifierLabel}</span>
            <input
              type="text"
              value={forgotIdentifier}
              onChange={(event) => setForgotIdentifier(event.target.value)}
              required
            />
          </label>
        </AuthCard>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title={<span className="auth-page-title">{siteTexts.auth.login.title}</span>}
      subtitle={siteTexts.auth.login.subtitle}
    >
      {successMessage && (
        <Toast
          message={successMessage}
          variant="success"
          onClose={() => setSuccessMessage("")}
        />
      )}
      <AuthCard
        error={error}
        onSubmit={onSubmit}
        submitAction={
          <div className="login-submit">
            <Button type="submit" variant="primary">
              {siteTexts.auth.login.submit}
            </Button>
          </div>
        }
        footer={
          <div className="login-register">
            <p>
              {siteTexts.auth.login.footerCta}{" "}
              <a href="#/cadastro">{siteTexts.auth.login.footerLink}</a>
            </p>
          </div>
        }
      >
        <label className="field">
          <span>{siteTexts.auth.login.identifierLabel}</span>
          <input
            type="text"
            value={identifier}
            onChange={(event) => setIdentifier(event.target.value)}
            required
          />
        </label>
        <label className="field">
          <span>{siteTexts.auth.login.passwordLabel}</span>
          <PasswordInput
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </label>
        <div className="login-actions">
          <button
            type="button"
            className="auth-forgot-password-link"
            onClick={() => {
              setShowForgotPassword(true);
              setError("");
              setSuccessMessage("");
              setForgotIdentifier(identifier);
            }}
          >
            {siteTexts.auth.forgotPassword.link}
          </button>
        </div>
      </AuthCard>
    </AppLayout>
  );
}
